import { useApprovalQuestionnaire, useGetVersion } from 'src/use-cases/invoice/useInvoices';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { useMemo } from 'react';
import { DeviationQuestionUserResponseDto, InvoiceSummaryDto } from 'src/generated/services/TFinancialApi';
import { useShipmentSelector } from 'src/presentations/components/organisms/actionDetails/ShipmentSelector';
import { useGetApprovalUserResponse, useUpdateApprovalUserResponse } from './useUserResponse';

export const useApprovalDecision = (invoiceSummary: InvoiceSummaryDto | null) => {
  const { shipment } = useShipmentSelector();
  const enabled = shipment && shipment?.approvalRequired;
  const { mutateAsync: updateResponse } = useUpdateApprovalUserResponse(shipment?.shipmentInvoiceId, invoiceSummary?.id);
  const { data: questionnaire } = useApprovalQuestionnaire(shipment?.shipmentInvoiceId, enabled);
  const { data: responseData } = useGetApprovalUserResponse(shipment?.shipmentInvoiceId, enabled);

  const operationsFlows = questionnaire?.OPERATION_REVIEW;
  const procurementFlows = questionnaire?.PROCUREMENT_REVIEW;

  const operationsResponse = responseData?.data?.OPERATION_REVIEW;
  const procurementResponse = responseData?.data?.PROCUREMENT_REVIEW;

  const { invoiceId, version, setIsShowErrorVersion } = useInvoiceContext();

  const { refetch: refetchVersion } = useGetVersion(invoiceId);
  const { canGiveOpsReview, canGiveProcurementReview } = useMemo(() => {
    const permissions = invoiceSummary?.permissions;
    if (!permissions) return {};
    return {
      canGiveOpsReview: permissions.includes('CAN_GIVE_OPERATION_REVIEW'),
      canGiveProcurementReview: permissions.includes('CAN_GIVE_PROCUREMENT_REVIEW'),
    };
  }, [invoiceSummary]);

  const isLoading = !questionnaire || !responseData || !invoiceSummary;

  const handleSubmit = async (data: DeviationQuestionUserResponseDto[]) => {
    const respVersion = await refetchVersion();

    if (version && respVersion?.data && version !== respVersion?.data) {
      setIsShowErrorVersion(true);
    } else {
      await updateResponse(data);
    }
  };

  return {
    updateResponse,
    questionnaire,
    responseData,
    operationsFlows,
    procurementFlows,
    operationsResponse,
    procurementResponse,
    invoiceSummary,
    invoiceId,
    canGiveOpsReview,
    canGiveProcurementReview,
    isLoading,
    handleSubmit,
  } as const;
};
